import axios from 'axios';

const api = axios.create({
  // Em produção com Nginx de borda, usamos proxy '/api' no mesmo domínio.
  // Em dev, docker-compose define VITE_API_URL=http://localhost:3000.
  baseURL: import.meta.env.VITE_API_URL || '/api',
});

api.interceptors.request.use(cfg => {
  const raw = localStorage.getItem('auth');
  if (raw) {
    try {
      const { accessToken } = JSON.parse(raw);
      if (accessToken) cfg.headers.Authorization = `Bearer ${accessToken}`;
    } catch (_) {}
  }
  return cfg;
});

api.interceptors.response.use(r => r, err => {
  const originalRequest = err.config || {};
  // Não tentar refresh na própria rota de refresh para evitar loop infinito
  if (err?.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
    // Tentar refresh se existir
    const raw = localStorage.getItem('auth');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed.refreshToken) {
          originalRequest._retry = true;
          return api.post('/auth/refresh', { refreshToken: parsed.refreshToken }).then(res => {
            const merged = { ...parsed, accessToken: res.data.accessToken, refreshToken: res.data.refreshToken };
            localStorage.setItem('auth', JSON.stringify(merged));
            originalRequest.headers.Authorization = `Bearer ${merged.accessToken}`;
            return api(originalRequest);
          }).catch(() => {
            localStorage.removeItem('auth');
            if (!location.pathname.startsWith('/login')) location.href = '/login';
            return Promise.reject(err);
          });
        }
      } catch (_) { /* noop */ }
    }
    localStorage.removeItem('auth');
    if (!location.pathname.startsWith('/login')) location.href = '/login';
    try { toastEmitter.emit({ type: 'info', message: 'Sessão expirada. Faça login novamente.' }); } catch {}
  }
  return Promise.reject(err);
});

export const AuthApi = {
  login: (email, senha) => api.post('/auth/login', { email, senha }).then(r=>r.data),
  alterarSenha: (dados) => api.post('/auth/change-password', dados).then(r=>r.data),
  me: () => api.get('/auth/me').then(r=>r.data),
};

export const MetricasApi = {
  listar: (params) => api.get('/metricas', { params }).then(r=>r.data),
  criar: (dados) => api.post('/metricas', dados).then(r=>r.data),
  atualizar: (id, dados) => api.patch(`/metricas/${id}`, dados).then(r=>r.data),
  seedInterval: (dados) => api.post('/metricas/seed-interval', dados).then(r=>r.data),
};

export const MetricasDesempenhoApi = {
  listar: (params) => api.get('/metricas-desempenho', { params }).then(r=>r.data),
  listarTodas: () => api.get('/metricas-desempenho/todos').then(r=>r.data),
  criar: (dados) => api.post('/metricas-desempenho', dados).then(r=>r.data),
  atualizar: (id, dados) => api.patch(`/metricas-desempenho/${id}`, dados).then(r=>r.data),
  deletar: (id) => api.delete(`/metricas-desempenho/${id}`).then(r=>r.data),
};

export const ColaboradoresApi = {
  listar: () => api.get('/colaboradores').then(r=>r.data),
  me: () => api.get('/colaboradores/me').then(r=>r.data),
  criar: (dados) => api.post('/colaboradores', dados).then(r=>r.data),
  obter: (id) => api.get(`/colaboradores/${id}`).then(r=>r.data),
  atualizar: (id, dados) => api.patch(`/colaboradores/${id}`, dados).then(r=>r.data),
};

export const UsuariosApi = {
  listar: (params) => api.get('/usuarios', { params }).then(r=>r.data),
  criar: (dados) => api.post('/usuarios', dados).then(r=>r.data),
  atualizar: (id, dados) => api.patch(`/usuarios/${id}`, dados).then(r=>r.data),
  deletar: (id) => api.delete(`/usuarios/${id}`).then(r=>r.data),
};

export const RelatoriosApi = {
  mensal: (colaboradorId, ano, mes) => api.get(`/relatorios/mensal/${colaboradorId}`, { params:{ ano, mes } }).then(r=>r.data),
  mensalExcelUrl: (colaboradorId, ano, mes) => `/relatorios/mensal/${colaboradorId}.xlsx?ano=${ano}&mes=${mes}`,
  mensalPdfUrl: (colaboradorId, ano, mes) => `/relatorios/mensal/${colaboradorId}.pdf?ano=${ano}&mes=${mes}`,
  fechamentoGeral: (ano, mes, { consolidado=false } = {}) => api.get('/relatorios/fechamento/geral', { params:{ ano, mes, consolidado } }).then(r=>r.data),
  fechamentoExcelUrl: (ano, mes) => `/relatorios/fechamento/geral.xlsx?ano=${ano}&mes=${mes}`,
  fechamentoPdfUrl: (ano, mes) => `/relatorios/fechamento/geral.pdf?ano=${ano}&mes=${mes}`,
};

export const FechamentoApi = {
  executar: (ano, mes) => api.post(`/fechamento/mes/${ano}/${mes}`).then(r=>r.data),
  status: (ano, mes) => api.get(`/fechamento/status/${ano}/${mes}`).then(r=>r.data),
};

export const ConfigApi = {
  obter: () => api.get('/config').then(r => r.data),
};

export const PenalidadesApi = {
  listar: () => api.get('/penalidades').then(r => r.data),
  criar: (dados) => api.post('/penalidades', dados).then(r => r.data),
  atualizar: (id, dados) => api.put(`/penalidades/${id}`, dados).then(r => r.data),
  deletar: (id) => api.delete(`/penalidades/${id}`).then(r => r.data),
};

export default api;
